import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import CommonApi from "../../../api/common.api";
import LectureApi from "../../../api/lecture.api";
import DelBtn from "../../../component/atoms/button/delBtn";
import SaveBtn from "../../../component/atoms/button/saveBtn";
import ImgInput from "../../../component/atoms/input/imgInput";
import MainInput from "../../../component/atoms/input/mainInput";
import DetailPage from "../../../component/atoms/layout/detail/detailPage";
import DetailPageForm from "../../../component/atoms/layout/detail/detailPageForm";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import DetailTop from "../../../component/atoms/layout/detail/detailTop";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import DetailFormTitle from "../../../component/atoms/text/detail/detailFormTitle";
import DetailPageTitle from "../../../component/atoms/text/detail/detailPageTitle";
import PretendardText from "../../../component/atoms/text/pretendardText";
import GoBack from "../../../component/molecules/goBack";
import VideoPreview from "../../../component/molecules/videoPreview";
import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";
import Modal from "../../../component/templates/Modal";
import { imgUrl } from "../../../constants/url";
import { isNull } from "../../../util/check";
import { errorHandler } from "../../../util/errorHandler";
import { modalMsg } from "../../../util/modalMsg";
import { toastMsg } from "../../../util/toastMsg";
import Loading from "../../../component/templates/Loading";

const CurriculumEdit = () => {
  const navigate = useNavigate();
  let { id } = useParams();

  const defaultForm = {
    title: "",
    content: "",
    file: "",
    fileUrl: "",
    videoOriginFileName: "",
    videoServerFileName: "",
  };

  // ----- 정보 가져오기
  const [curriculumInfo, setCurriculumInfo] = useState([]);

  useEffect(() => {
    getCurriculumList();
  }, [id]);

  const getCurriculumList = async () => {
    try {
      const data = {
        lectureVideoId: id,
        orderBy: "ASC",
        size: 0,
      };

      const response = (await LectureApi.getCurriculum(data)).data.data.content;

      setCurriculumInfo(response);
    } catch (error) {
      errorHandler(error);
    }
  };

  const delImg = (index) => {
    const copyArr = [...curriculumInfo];
    copyArr[index].file = "";
    copyArr[index].fileUrl = "";
    copyArr[index].videoOriginFileName = "";
    copyArr[index].videoServerFileName = "";

    setCurriculumInfo(copyArr);
  };

  const inputFile = (e, index) => {
    const file = e.target.files[0];

    if (!file) return;
    if (!file.type.startsWith("video/")) {
      e.target.value = "";
      return toast("영상 파일만 등록할 수 있습니다.");
    }

    const copyArr = [...curriculumInfo];
    copyArr[index].file = file;

    // 비디오 파일인지 확인
    const videoUrl = URL.createObjectURL(file);
    copyArr[index].fileUrl = videoUrl;

    // 비디오의 재생 시간(playTime) 얻기
    const videoElement = document.createElement("video");
    videoElement.src = videoUrl;

    videoElement.onloadedmetadata = () => {
      const playTime = videoElement.duration; // 재생 시간 (초 단위)
      copyArr[index].playTime = Math.round(playTime); // 소수점 반올림
      setCurriculumInfo(copyArr);
    };

    e.target.value = "";
  };

  //저장하기
  const [saveId, setSaveId] = useState(-1);
  const [saveIndex, setSaveIndex] = useState(-1);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveNewModalOpen, setSaveNewModalOpen] = useState(false);

  const saveBtnHandler = (index) => {
    const item = curriculumInfo[index];
    if (
      !item ||
      isNull(item.title?.trim()) ||
      isNull(item.content?.trim()) ||
      (isNull(item.file) && isNull(item.videoServerFileName))
    ) {
      return toast("영상, 제목, 설명을 모두 입력해주세요.");
    }

    setSaveModalOpen(true);
  };

  const newSaveBtnHandler = () => {
    const newItems = curriculumInfo.filter((item) => !item.id);
    if (newItems.length === 0) {
      return toast("새로운 커리큘럼을 1개 이상 추가해주세요");
    }

    const invalidIndex = newItems.findIndex(
      (item) =>
        isNull(item.title?.trim()) ||
        isNull(item.content?.trim()) ||
        (isNull(item.file) && isNull(item.videoServerFileName))
    );
    if (invalidIndex !== -1) {
      return toast(
        `새 커리큘럼 ${invalidIndex + 1}번의 영상, 제목, 설명을 모두 입력해주세요.`
      );
    }

    setSaveNewModalOpen(true);
  };

  const saveFunc = async () => {
    try {
      const data = {
        title: curriculumInfo[saveIndex].title,
        content: curriculumInfo[saveIndex].content,
        videoOriginFileName: curriculumInfo[saveIndex].videoOriginFileName,
        videoServerFileName: curriculumInfo[saveIndex].videoServerFileName,
        playTime: curriculumInfo[saveIndex].playTime,
      };

      if (!isNull(curriculumInfo[saveIndex].file)) {
        const file = curriculumInfo[saveIndex].file;
        const result = await fileUpload(file, (progressEvent) => {
          setUploadProgress(
            file.size > 0
              ? Math.min(100, Math.round((progressEvent.loaded / file.size) * 100))
              : 100
          );
        });
        data.videoOriginFileName = result.imageOriginFileName;
        data.videoServerFileName = result.imageServerFileName;
      }

      await LectureApi.editCurriculum(saveId, data);
      toast(toastMsg.update);

      setSaveModalOpen(false);
      await getCurriculumList();
    } catch (error) {
      errorHandler(error);
      toast("영상 업로드 또는 저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setUploadLoading(false);
      setSaveModalOpen(false);
    }
  };

  const [uploadLoading, setUploadLoading] = useState(false);

  const newSaveFunc = async () => {
    try {
      const newItems = curriculumInfo.filter((item) => !item.id);
      const totalBytes = newItems.reduce(
        (total, item) => total + (item.file?.size || 0),
        0
      );
      const uploadedBytes = {};
      const list = await Promise.all(
        newItems.map(async (item, index) => {
            const data = {
              title: item.title,
              content: item.content,
              videoOriginFileName: item.videoOriginFileName,
              videoServerFileName: item.videoServerFileName,
              playTime: item.playTime,
            };

            // 파일이 있는 경우 업로드 처리
            if (!isNull(item.file)) {
              const result = await fileUpload(item.file, (progressEvent) => {
                uploadedBytes[index] = progressEvent.loaded;
                const loaded = Object.values(uploadedBytes).reduce(
                  (total, value) => total + value,
                  0
                );
                setUploadProgress(
                  totalBytes > 0
                    ? Math.min(100, Math.round((loaded / totalBytes) * 100))
                    : 100
                );
              });
              data.videoOriginFileName = result.imageOriginFileName;
              data.videoServerFileName = result.imageServerFileName;
            }

            return data; // 비동기 작업 결과 반환
          })
      );

      await LectureApi.addCurriculum(id, { lectureCurriculumList: list });
      toast(toastMsg.update);

      setSaveNewModalOpen(false);
      setUploadProgress(100);
      await getCurriculumList();
    } catch (error) {
      errorHandler(error);
      toast("영상 업로드 또는 저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setUploadLoading(false);
      setSaveNewModalOpen(false);
    }
  };

  // 파일 업로드
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileUpload = async (file, onUploadProgress) => {
    try {
      const response = (
        await CommonApi.getFileUploadUrl({
          path: "lecture",
          originalFileName: file.name,
          contentType: file.type || "application/octet-stream",
        })
      ).data.data;

      await CommonApi.cloudFileUpload(response.uploadUrl, file, onUploadProgress);

      return {
        imageOriginFileName: file.name,
        imageServerFileName: response.serverFileName,
      };
    } catch (directUploadError) {
      const formData = new FormData();
      formData.append("path", "lecture");
      formData.append("file", file);

      const response = (
        await CommonApi.fileUpload(formData, onUploadProgress)
      ).data.data;

      return {
        imageOriginFileName: file.name,
        imageServerFileName: response.serverFileName,
      };
    }
  };

  const addCurriculum = () => {
    setCurriculumInfo((prevInfo) => [...prevInfo, defaultForm]);
  };

  // ----- 커리큘럼 삭제
  const [delId, setDelId] = useState(-1);
  const [delModalOpen, setDelModalOpen] = useState(false);

  const delCurriculum = async () => {
    try {
      const data = {
        ids: [delId],
      };
      await LectureApi.delCurriculum(data);

      toast(toastMsg.delete);
      setDelModalOpen(false);
      setDelId(-1);
      getCurriculumList();
    } catch (error) {
      errorHandler(error);
    }
  };

  const editCurriculum = async () => {
    try {
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout>
        <Sidebar />
        <DetailPageLayout>
          <DetailTop>
            <GoBack />
          </DetailTop>
          {curriculumInfo.map((item, index) => {
            return (
              <DetailPage key={index}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <DetailPageTitle>커리큘럼 {index + 1}강</DetailPageTitle>
                  <DelBtn
                    style={{
                      borderColor: "#DFE1E4",
                      padding: "0 8px",
                      borderRadius: "4px",
                      height: "25px",
                      fontSize: "14px",
                    }}
                    onClick={() => {
                      setDelId(item.id);
                      setDelModalOpen(true);
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <DetailPageForm style={{ alignItems: "flex-start" }}>
                    <DetailFormTitle>영상 업로드*</DetailFormTitle>
                    {item.videoServerFileName || item.fileUrl ? (
                      <VideoPreview
                        fileUrl={item.fileUrl ? item.fileUrl : imgUrl + item.videoServerFileName}
                        onClick={() => {
                          delImg(index);
                        }}
                      />
                    ) : (
                      <ImgInput
                        id={`lecture${index}`}
                        accept={"video/*"}
                        onChange={(e) => {
                          inputFile(e, index);
                        }}
                      />
                    )}
                  </DetailPageForm>
                  <DetailPageForm>
                    <DetailFormTitle>제목*</DetailFormTitle>
                    <MainInput
                      style={{
                        width: "338px",
                      }}
                      type={"text"}
                      placeholder={"제목을 입력해 주세요."}
                      value={item.title}
                      onChange={(e) => {
                        const copyArr = [...curriculumInfo];
                        copyArr[index].title = e.target.value;
                        setCurriculumInfo(copyArr);
                      }}
                    />
                  </DetailPageForm>
                  <DetailPageForm>
                    <DetailFormTitle>설명*</DetailFormTitle>
                    <MainInput
                      style={{
                        width: "744px",
                      }}
                      type={"text"}
                      placeholder={"내용을 입력해주세요"}
                      value={item.content}
                      onChange={(e) => {
                        const copyArr = [...curriculumInfo];
                        copyArr[index].content = e.target.value;
                        setCurriculumInfo(copyArr);
                      }}
                    />
                  </DetailPageForm>
                </div>
                {item.id && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "30px",
                      }}
                    >
                      <SaveBtn
                        onClick={() => {
                          setSaveId(item.id);
                          setSaveIndex(index);
                          saveBtnHandler(index);
                        }}
                      />
                    </div>
                  </div>
                )}
              </DetailPage>
            );
          })}

          <AddBtn
            style={{
              backgroundColor: "#A4A9AD",
              border: "none",
              marginBottom: "20px",
            }}
            onClick={addCurriculum}
          >
            <PretendardText
              style={{
                color: "#FFFFFF",
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              + 커리큘럼 추가
            </PretendardText>
          </AddBtn>
          <AddBtn
            onClick={() => {
              newSaveBtnHandler();
            }}
          >
            <PretendardText
              style={{
                color: "#FFFFFF",
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              새롭게 추가된 강의 저장하기
            </PretendardText>
          </AddBtn>
        </DetailPageLayout>
      </MainLayout>

      {saveModalOpen ? (
        <Modal
          title={modalMsg.update?.title}
          text={modalMsg.update?.content}
          setModalOpen={setSaveModalOpen}
          onClick={() => {
            setUploadProgress(0);
            setUploadLoading(true);
            saveFunc();
          }}
        />
      ) : null}

      {!uploadLoading && saveNewModalOpen ? (
        <Modal
          title={modalMsg.update?.title}
          text={"새로운 커리큘럼들을 저장하시겠습니까?"}
          setModalOpen={setSaveNewModalOpen}
          onClick={() => {
            setUploadProgress(0);
            setUploadLoading(true);
            newSaveFunc();
          }}
        />
      ) : null}

      {delModalOpen ? <Modal title={modalMsg.delete.title} text={modalMsg.delete.content} setModalOpen={setDelModalOpen} onClick={delCurriculum} /> : null}

      {uploadLoading && (
        <ModalWrapper>
          <Loading
            title={
              uploadProgress > 0
                ? `파일 업로드 중입니다 (${uploadProgress}%)`
                : "파일 업로드 중입니다"
            }
          />
        </ModalWrapper>
      )}
    </>
  );
};

export default CurriculumEdit;

const AddBtn = styled.div`
  border: 1px solid #636c73;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  cursor: pointer;
  background-color: #636c73;
`;

const ModalWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #00000060;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 99;
`;
